import axios from 'axios'
import storage from 'store'
import request from '@/utils/request'

// 阿里云 Model Studio (DashScope) API 配置
const DASHSCOPE_API_KEY = 'sk-5653c62ca462467ca409a3524489d315'
const DASHSCOPE_BASE_URL = 'https://dashscope.aliyuncs.com/api/v1/apps'

// 阿里云 FunASR 语音识别配置
const FUNASR_CONFIG = {
  // 获取Token的API地址（需要后端提供）
  TOKEN_API: '/api/funasr/token',
  // WebSocket服务地址
  // 注意：根据阿里云文档，正确的地址格式
  WS_URL: 'wss://nls-gateway-cn-shanghai.aliyuncs.com/ws/v1'
}

/**
 * 调用智能体应用（标准调用）
 * @param {string} appId - 应用ID
 * @param {string} prompt - 用户输入的问题
 * @param {object} options - 可选参数
 * @param {string[]} options.imageList - 图片URL列表，用于图片识别（参考：https://help.aliyun.com/zh/model-studio/call-single-agent-application/#4f5e6ac27cakp）
 * @returns {Promise<object>} 返回结果对象 {success, data, text, requestId}
 */
export async function callAgent(appId, prompt, options = {}) {
  console.log('调用智能体应用（标准调用）');
  
  if (!appId) {
    return Promise.reject(new Error('应用ID不能为空'))
  }

  // 如果没有文本也没有图片，则报错
  const hasPrompt = prompt && typeof prompt === 'string' && prompt.trim()
  const hasImage = options.imageList && Array.isArray(options.imageList) && options.imageList.length > 0
  
  if (!hasPrompt && !hasImage) {
    return Promise.reject(new Error('问题或图片不能同时为空'))
  }

  const url = `${DASHSCOPE_BASE_URL}/${appId}/completion`

  // 获取租户ID
  const tenantId = storage.get('tenantId')

  // 提取 imageList 从 options
  const { imageList, ...restOptions } = options

  // 根据阿里云 Model Studio 文档，智能体应用调用格式
  // 使用 input 对象包装 prompt、image_list 和自定义参数
  const inputData = {
    // 如果有 prompt，则添加 prompt 字段
    ...(hasPrompt && {
      prompt: prompt.trim()
    }),
    // 如果有图片，添加 image_list 参数（参考文档：https://help.aliyun.com/zh/model-studio/call-single-agent-application/#4f5e6ac27cakp）
    ...(imageList && imageList.length > 0 && {
      image_list: imageList
    }),
    // 传递自定义参数 tenant_id（通过 biz_params）
    ...(tenantId && {
      biz_params: {
        user_prompt_params: {
          tenant_id: tenantId
        }
      }
    })
  }

  const requestData = {
    input: inputData,
    ...restOptions
  }

  try {
    const response = await axios.post(url, requestData, {
      headers: {
        Authorization: `Bearer ${DASHSCOPE_API_KEY}`,
        'Content-Type': 'application/json',
        'X-DashScope-SSE': 'disable' // 禁用 SSE
      },
      timeout: 600000 // 600秒超时
    })

    // 处理响应
    if (response.status === 200 && response.data) {
      const data = response.data

      // 尝试多种可能的响应格式
      let text = ''
      if (data.output) {
        // 格式1: output.text
        text = data.output.text || ''
        // 格式2: output.choices[0].message.content
        if (!text && data.output.choices && data.output.choices[0]) {
          text = data.output.choices[0].message?.content || data.output.choices[0].text || ''
        }
      }
      // 格式3: 直接包含text字段
      if (!text && data.text) {
        text = data.text
      }
      // 格式4: 直接包含content字段
      if (!text && data.content) {
        text = data.content
      }

      return {
        success: true,
        data: data,
        text: text || '未获取到回复内容',
        requestId: data.request_id || data.requestId || response.headers['x-request-id']
      }
    } else {
      return Promise.reject(new Error('响应格式错误'))
    }
  } catch (error) {
    console.error('调用智能体失败:', error)

    // 处理错误响应
    if (error.response) {
      const status = error.response.status
      const errorData = error.response.data

      console.error('API错误响应:', {
        status,
        data: errorData,
        headers: error.response.headers
      })

      let errorMessage = '调用智能体失败'
      if (errorData) {
        // 尝试多种错误信息格式
        if (errorData.message) {
          errorMessage = errorData.message
        } else if (errorData.error && errorData.error.message) {
          errorMessage = errorData.error.message
        } else if (errorData.code) {
          errorMessage = `错误代码: ${errorData.code}`
        } else if (typeof errorData === 'string') {
          errorMessage = errorData
        }
      }

      if (status === 400) {
        errorMessage = `请求参数错误: ${errorMessage}。请检查应用ID和输入内容是否正确。`
      } else if (status === 401) {
        errorMessage = 'API密钥无效，请检查配置'
      } else if (status === 404) {
        errorMessage = '应用ID不存在，请检查配置'
      } else if (status === 429) {
        errorMessage = '请求过于频繁，请稍后重试'
      } else if (status >= 500) {
        errorMessage = '服务器错误，请稍后重试'
      }

      return Promise.reject(new Error(errorMessage))
    } else if (error.request) {
      return Promise.reject(new Error('网络请求失败，请检查网络连接'))
    } else {
      return Promise.reject(new Error(error.message || '未知错误'))
    }
  }
}

/**
 * 调用智能体应用（流式调用）
 * 根据阿里云百炼控制台文档实现：https://bailian.console.aliyun.com/?tab=doc#/doc/?type=app&url=2854620
 * 
 * @param {string} appId - 应用ID
 * @param {string} prompt - 用户输入的问题
 * @param {function} onMessage - 接收消息的回调函数 (text: string) => void，每次接收到文本片段时调用
 * @param {object} options - 可选参数
 * @param {string[]} options.imageList - 图片URL列表，用于图片识别（参考：https://help.aliyun.com/zh/model-studio/call-single-agent-application/#4f5e6ac27cakp）
 * @returns {Promise<object>} 返回结果对象 {success: boolean, text: string, requestId: string}
 * 
 * 流式调用特点：
 * 1. 使用 SSE (Server-Sent Events) 协议
 * 2. 实时接收文本片段，提升用户体验
 * 3. 支持多种响应格式（完整文本、增量文本等）
 * 4. 自动处理跨chunk的数据分割
 * 5. 支持图片识别（通过 image_list 参数）
 */
export async function callAgentStream(appId, prompt, onMessage, options = {}) {
  console.log('调用智能体应用（流式调用）');
  
  if (!appId) {
    return Promise.reject(new Error('应用ID不能为空'))
  }

  // 如果没有文本也没有图片，则报错
  const hasPrompt = prompt && typeof prompt === 'string' && prompt.trim()
  const hasImage = options.imageList && Array.isArray(options.imageList) && options.imageList.length > 0
  
  if (!hasPrompt && !hasImage) {
    return Promise.reject(new Error('问题或图片不能同时为空'))
  }

  if (typeof onMessage !== 'function') {
    return Promise.reject(new Error('onMessage必须是一个函数'))
  }

  const url = `${DASHSCOPE_BASE_URL}/${appId}/completion`

  // 获取租户ID
  const tenantId = storage.get('tenantId')

  // 提取 imageList 从 options
  const { imageList, ...restOptions } = options

  // 根据阿里云 Model Studio 文档，流式调用格式
  // 使用 input 对象包装 prompt、image_list 和自定义参数
  const inputData = {
    // 如果有 prompt，则添加 prompt 字段
    ...(hasPrompt && {
      prompt: prompt.trim()
    }),
    // 如果有图片，添加 image_list 参数（参考文档：https://help.aliyun.com/zh/model-studio/call-single-agent-application/#4f5e6ac27cakp）
    ...(imageList && imageList.length > 0 && {
      image_list: imageList
    }),
    // 传递自定义参数 tenant_id（通过 biz_params）
    ...(tenantId && {
      biz_params: {
        user_prompt_params: {
          tenant_id: tenantId
        }
      }
    })
  }

  const requestData = {
    input: inputData,
    stream: true, // 启用流式输出
    ...restOptions
  }

  try {
    // 使用 Fetch API 处理 SSE 流式数据（浏览器环境）
    // Fetch API 支持 ReadableStream，可以正确处理流式响应
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${DASHSCOPE_API_KEY}`,
        'Content-Type': 'application/json',
        'X-DashScope-SSE': 'enable' // 启用 SSE 流式输出
      },
      body: JSON.stringify(requestData)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
    }

    // 检查响应是否包含流式数据
    if (!response.body) {
      throw new Error('响应不包含流式数据')
    }

    return new Promise((resolve, reject) => {
      let fullText = ''
      let requestId = ''
      let buffer = '' // 用于处理跨chunk的SSE数据

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      // 读取流式数据
      const readChunk = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            // 流结束，处理buffer中剩余的数据
            if (buffer.trim()) {
              const trimmedLine = buffer.trim()
              if (trimmedLine.startsWith('data: ')) {
                try {
                  const jsonStr = trimmedLine.slice(6)
                  if (jsonStr !== '[DONE]') {
                    const data = JSON.parse(jsonStr)
                    if (data.output && data.output.text) {
                      const text = data.output.text
                      const deltaText = text.slice(fullText.length)
                      if (deltaText) {
                        fullText = text
                        onMessage(deltaText)
                      }
                    }
                  }
                } catch (e) {
                  console.warn('处理buffer数据时出错:', e)
                }
              }
            }

            resolve({
              success: true,
              text: fullText,
              requestId: requestId
            })
            return
          }

          // 将chunk添加到buffer中
          const chunkStr = decoder.decode(value, { stream: true })
          buffer += chunkStr
          // 调试：打印原始chunk数据（开发时使用）
          console.log('收到SSE数据chunk:', chunkStr.substring(0, 200))
          
          // 按行分割处理SSE格式数据
          const lines = buffer.split('\n')
          // 保留最后一个不完整的行（可能跨chunk）
          buffer = lines.pop() || ''

          for (const line of lines) {
            const trimmedLine = line.trim()
            
            // 跳过空行
            if (!trimmedLine) {
              continue
            }
            
            // 处理SSE注释行（以:开头，如 :HTTP_STATUS/200）
            if (trimmedLine.startsWith(':')) {
              console.log('SSE注释:', trimmedLine)
              continue
            }
            
            // 处理SSE id字段（如 id:183）
            if (trimmedLine.startsWith('id:')) {
              const id = trimmedLine.slice(3).trim()
              console.log('SSE id:', id)
              continue
            }
            
            // 处理SSE event字段（如 event:result）
            if (trimmedLine.startsWith('event:')) {
              const eventType = trimmedLine.slice(6).trim()
              console.log('SSE event:', eventType)
              continue
            }

            // 处理SSE格式：data: {...} 或 data:{...}
            if (trimmedLine.startsWith('data:')) {
              try {
                // 移除 'data:' 或 'data: ' 前缀
                let jsonStr = trimmedLine.startsWith('data: ') 
                  ? trimmedLine.slice(6) 
                  : trimmedLine.slice(5)
                jsonStr = jsonStr.trim()
                
                // 处理 [DONE] 标记（表示流结束）
                if (jsonStr === '[DONE]') {
                  console.log('收到流结束标记 [DONE]')
                  continue
                }
                
                // 如果为空，跳过
                if (!jsonStr) {
                  continue
                }
                
                // 如果JSON字符串不完整（可能跨chunk），检查括号是否匹配
                if (!jsonStr.startsWith('{') && !jsonStr.startsWith('[')) {
                  console.warn('JSON格式异常，跳过:', jsonStr.substring(0, 50))
                  continue
                }
                
                // 检查JSON是否完整（简单检查：开头和结尾括号匹配）
                const isComplete = (jsonStr.startsWith('{') && jsonStr.endsWith('}')) ||
                                   (jsonStr.startsWith('[') && jsonStr.endsWith(']'))
                
                if (!isComplete) {
                  // JSON不完整，将整行保留到buffer中等待后续数据
                  console.log('JSON不完整，等待后续数据...')
                  buffer = trimmedLine + '\n' + buffer
                  continue
                }
                
                // 尝试解析JSON
                let data
                try {
                  data = JSON.parse(jsonStr)
                } catch (parseError) {
                  // JSON可能不完整或格式错误
                  console.warn('JSON解析失败:', parseError.message)
                  console.warn('JSON字符串:', jsonStr.substring(0, 200))
                  // 尝试将整行保留到buffer中
                  buffer = trimmedLine + '\n' + buffer
                  continue
                }
                console.log('解析后的SSE数据:', JSON.stringify(data).substring(0, 500))
                console.log('完整数据结构:', data)

                // 检查 reject_status
                if (data.output && data.output.reject_status === true) {
                  console.warn('收到拒绝状态，可能需要检查请求参数')
                  // 即使被拒绝，也继续处理，可能有错误信息
                }
                
                // 检查 finish_reason
                if (data.output && data.output.finish_reason) {
                  console.log('finish_reason:', data.output.finish_reason)
                  // 如果 finish_reason 不是 null，可能表示流即将结束
                  if (data.output.finish_reason !== 'null' && data.output.finish_reason !== null) {
                    console.log('流即将结束，finish_reason:', data.output.finish_reason)
                  }
                }

                // 提取文本内容（支持多种格式）
                let text = ''
                let hasText = false
                
                // 格式1: output.text（完整文本或增量文本）
                if (data.output && data.output.text !== undefined && data.output.text !== null) {
                  const outputText = String(data.output.text) // 确保是字符串
                  
                  // 如果text不为空，处理它
                  if (outputText) {
                    // 阿里云流式返回的text通常是累积的完整文本
                    // 计算增量：新文本 - 已有文本
                    if (outputText.length >= fullText.length) {
                      // 新文本长度大于等于已有文本，说明是累积的完整文本
                      const deltaText = outputText.slice(fullText.length)
                      if (deltaText) {
                        fullText = outputText
                        console.log('格式1(完整文本) - 发送增量文本:', deltaText.substring(0, 100))
                        onMessage(deltaText)
                        hasText = true
                      }
                    } else {
                      // 新文本长度小于已有文本，可能是新的片段，直接追加
                      const deltaText = outputText
                      fullText += deltaText
                      console.log('格式1(增量文本) - 发送增量文本:', deltaText.substring(0, 100))
                      onMessage(deltaText)
                      hasText = true
                    }
                  } else {
                    console.log('output.text 为空字符串，跳过')
                  }
                }
                
                // 如果output.text没有提取到，检查其他字段
                if (!hasText && data.output) {
                  
                  // 检查 output.message
                  if (!hasText && data.output.message) {
                    if (typeof data.output.message === 'string') {
                      text = data.output.message
                    } else if (data.output.message.content) {
                      text = data.output.message.content
                    }
                    if (text) {
                      const deltaText = text.slice(fullText.length)
                      if (deltaText) {
                        fullText = text
                        console.log('格式1(message) - 发送增量文本:', deltaText.substring(0, 50))
                        onMessage(deltaText)
                        hasText = true
                      }
                    }
                  }
                  
                  // 检查 output.content
                  if (!hasText && data.output.content) {
                    text = data.output.content
                    const deltaText = text.slice(fullText.length)
                    if (deltaText) {
                      fullText = text
                      console.log('格式1(content) - 发送增量文本:', deltaText.substring(0, 50))
                      onMessage(deltaText)
                      hasText = true
                    }
                  }
                }
                
                // 格式2: output.choices[0].delta.content（增量文本）
                if (!hasText && data.output && data.output.choices && data.output.choices[0]) {
                  const choice = data.output.choices[0]
                  if (choice.delta && choice.delta.content) {
                    text = choice.delta.content
                    fullText += text
                    console.log('格式2 - 发送增量文本:', text.substring(0, 50))
                    onMessage(text)
                    hasText = true
                  } else if (choice.message && choice.message.content) {
                    text = choice.message.content
                    const deltaText = text.slice(fullText.length)
                    if (deltaText) {
                      fullText = text
                      console.log('格式2(完整) - 发送增量文本:', deltaText.substring(0, 50))
                      onMessage(deltaText)
                      hasText = true
                    }
                  }
                }
                
                // 格式3: 直接包含text字段
                if (!hasText && data.text) {
                  text = data.text
                  const deltaText = text.slice(fullText.length)
                  if (deltaText) {
                    fullText = text
                    console.log('格式3 - 发送增量文本:', deltaText.substring(0, 50))
                    onMessage(deltaText)
                    hasText = true
                  }
                }
                
                // 格式4: 直接包含content字段
                if (!hasText && data.content) {
                  text = data.content
                  const deltaText = text.slice(fullText.length)
                  if (deltaText) {
                    fullText = text
                    console.log('格式4 - 发送增量文本:', deltaText.substring(0, 50))
                    onMessage(deltaText)
                    hasText = true
                  }
                }

                // 如果没有提取到文本，记录详细的数据结构以便调试
                if (!hasText) {
                  // 只在第一次或每10次记录一次，避免日志过多
                  if (!window._sseDebugCount) window._sseDebugCount = 0
                  window._sseDebugCount++
                  
                  if (window._sseDebugCount <= 3 || window._sseDebugCount % 10 === 0) {
                    console.log(`未找到文本内容 (第${window._sseDebugCount}次)`)
                    console.log('数据结构:', Object.keys(data))
                    if (data.output) {
                      console.log('output结构:', Object.keys(data.output))
                      console.log('output内容:', JSON.stringify(data.output).substring(0, 200))
                    }
                  }
                  
                  // 如果这是流中的正常情况（某些chunk可能没有文本），继续处理
                  // 不中断流，等待后续chunk
                }

                // 提取request_id
                if (data.request_id) {
                  requestId = data.request_id
                } else if (data.requestId) {
                  requestId = data.requestId
                }

                // 处理错误响应
                if (data.error) {
                  const errorMsg = data.error.message || data.error.code || '流式响应错误'
                  console.error('流式响应错误:', data.error)
                  reject(new Error(errorMsg))
                  return
                }
                
                // 处理 reject_status（如果持续被拒绝且没有文本）
                if (data.output && data.output.reject_status === true && !hasText) {
                  // 如果已经接收了一些文本，不中断；如果完全没有文本，记录警告
                  if (fullText.length === 0) {
                    console.warn('请求被拒绝且没有返回文本，可能需要检查请求参数或权限')
                    // 不立即reject，等待后续chunk，可能后续会有错误信息
                  }
                }
              } catch (e) {
                // JSON解析错误，记录但不中断流
                console.warn('SSE数据解析错误:', e, '原始数据:', trimmedLine.substring(0, 200))
              }
            }
            // 处理event类型（如error事件）
            else if (trimmedLine.startsWith('event: ')) {
              const eventType = trimmedLine.slice(7)
              console.log('收到SSE事件:', eventType)
              if (eventType === 'error') {
                // 等待下一个data行获取错误信息
                console.warn('收到error事件:', eventType)
              }
            }
          }

          // 继续读取下一个chunk
          readChunk()
        }).catch((error) => {
          console.error('读取流式数据错误:', error)
          reject(new Error(`流式响应错误: ${error.message || '未知错误'}`))
        })
      }

      // 开始读取流
      readChunk()
    })
  } catch (error) {
    console.error('调用智能体失败:', error)

    // Fetch API 的错误处理
    if (error.message) {
      // 如果错误信息已经包含HTTP状态码，直接返回
      if (error.message.includes('HTTP')) {
        return Promise.reject(error)
      }
      
      // 根据错误信息判断错误类型
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        return Promise.reject(new Error('API密钥无效，请检查配置'))
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        return Promise.reject(new Error('应用ID不存在，请检查配置'))
      } else if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
        return Promise.reject(new Error('请求过于频繁，请稍后重试'))
      } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        return Promise.reject(new Error('服务器错误，请稍后重试'))
      } else if (error.message.includes('网络') || error.message.includes('network') || error.message.includes('fetch')) {
        return Promise.reject(new Error('网络请求失败，请检查网络连接'))
      }
    }

    return Promise.reject(new Error(error.message || '调用智能体失败'))
  }
}

/**
 * 获取阿里云 FunASR 语音识别 Token
 * 注意：Token获取应该在服务端完成，这里调用后端API
 * @returns {Promise<string>} 返回Token字符串
 */
export async function getFunASRToken() {
  try {
    // 方案1：如果后端提供了Token获取接口
    const response = await request({
      url: FUNASR_CONFIG.TOKEN_API,
      method: 'get'
    })
    
    if (response && response.data && response.data.token) {
      return response.data.token
    }
    
    throw new Error('Token获取失败：响应格式错误')
  } catch (error) {
    console.error('获取FunASR Token失败:', error)
    
    // 方案2：如果后端未提供接口，返回错误提示
    // 实际应用中，应该由后端提供Token获取接口
    throw new Error('请配置后端Token获取接口，或联系管理员配置FunASR服务')
  }
}
